import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { signIn, signUp } from "../services/auth";
import Button from "./Button";
import Input from "./Input";
import Spinner from "./Spinner";

interface AuthFormProps {
  mode: "login" | "signup";
  onSuccess: () => void;
}

export function AuthForm({ mode, onSuccess }: AuthFormProps) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [avatar, setAvatar] = useState("");
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          const dataUrl = event.target.result as string;
          setAvatarPreview(dataUrl);
          setAvatar(dataUrl);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (mode === "login") {
        await signIn(username, password);
        toast.success("Login successful!");
      } else {
        await signUp(email, password, firstName, lastName, avatar);
        toast.success("Admin account created successfully!");
      }
      onSuccess();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "An unexpected error occurred"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full xs:px-4 px-2 max-w-md mx-auto py-6 bg-white bg-opacity-90 rounded-lg shadow-md border-t-4 border-orange-500">
      <h2 className="text-lg xs:text-2xl font-bold mb-4 text-center text-gray-800">
        {mode === "login" ? "Access Admin Panel" : "Create Admin Account"}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        {mode === "signup" && (
          <>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Avatar (Profile Picture)
              </label>
              <div className="flex flex-col xs:flex-row justify-center items-center xs:space-x-4 space-y-4 xs:space-y-0">
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`w-20 h-20 xs:w-24 xs:h-24 rounded-full overflow-hidden border-2 cursor-pointer ${
                    avatarPreview
                      ? "border-orange-500"
                      : "border-dashed border-gray-300"
                  } flex items-center justify-center bg-gray-50 hover:bg-orange-50 transition-colors`}
                >
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="Avatar preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-gray-400 text-xs text-center">
                      No image
                    </span>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            </div>

            <Input
              type="text"
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              placeholder="Your first name"
            />
            <Input
              type="text"
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Your last name"
            />
          </>
        )}

        {mode === "login" && (
          <Input
            type="text"
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            placeholder="Your username"
          />
        )}

        {mode === "signup" && (
          <Input
            type="email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="your@email.com"
          />
        )}

        <Input
          type="password"
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          placeholder="Your password"
        />

        <Button type="submit" disabled={isLoading} className="w-full">
          {isLoading ? (
            <Spinner size="sm" />
          ) : mode === "login" ? (
            "Sign In"
          ) : (
            "Create admin account"
          )}
        </Button>
      </form>

      {mode === "login" && (
        <div className="mt-4 text-center">
          <Link
            to="/forgot-password"
            className="text-sm text-orange-600 hover:text-orange-700 transition-colors"
          >
            Forgot your password?
          </Link>
        </div>
      )}

      <p className="mt-4 text-center text-sm text-gray-600">
        {mode === "login" ? (
          <>
            Don&apos;t have an admin account?{" "}
            <Link
              to="/signup"
              className="text-orange-600 hover:text-orange-700 transition-colors font-medium"
            >
              Request access
            </Link>
          </>
        ) : (
          <>
            Already have an account?{" "}
            <Link to="/login" className="text-orange-600 hover:text-orange-700 transition-colors font-medium">
              Sign in
            </Link>
          </>
        )}
      </p>
    </div>
  );
}
